import { provideRouter } from '@angular/router';
import routeConfig from './app/routes';

import { bootstrapApplication, provideProtractorTestingSupport } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { akitaDevtools, DevtoolsOptions, enableAkitaProdMode } from '@datorama/akita';
import { ENVIRONMENT_INITIALIZER, ErrorHandler, importProvidersFrom, inject, NgZone } from '@angular/core';

import { environment } from './environments/environment';
import { IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalRedirectComponent,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { msalConfig, msalGuardConfig, msalInterceptorConfig } from 'src/app/shared/modules/authentication/auth-config';
import { OpenAPI } from './api';
import { ErrorDialogService } from './app/shared/services/error-dialog.service';
import { MatDialogModule } from '@angular/material/dialog';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DatePipe, registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import { LocalizedDatePipe } from './app/shared/localizedDatePipe';
import { GlobalErrorHandler } from './app/errors/globalErrorHandler';

//add openAPI base url depending apiEndpoint
OpenAPI.BASE = environment.apiEndpoint;

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}msalConfig

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return msalGuardConfig;
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  return msalInterceptorConfig;
}

if (environment.production) {
  enableAkitaProdMode();
}

//add languages to date pipe
registerLocaleData(en);
registerLocaleData(es);

bootstrapApplication(AppComponent, {
  providers: [
    provideProtractorTestingSupport(),
    provideRouter(routeConfig),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideAkitaDevtools(),
    importProvidersFrom(MatDialogModule),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      })
    ),
    DatePipe,
    importProvidersFrom(MsalRedirectComponent),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    ErrorDialogService,
    LocalizedDatePipe,
  ],
}).catch((err) => console.error(err));

export function provideAkitaDevtools(options: Partial<DevtoolsOptions> = {}) {
  return {
    provide: ENVIRONMENT_INITIALIZER,
    multi: true,
    useFactory() {
      return () => {
        return environment.production ? null : akitaDevtools(inject(NgZone), options);
      };
    },
  };
}

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
